import { ChangeDetectionStrategy, Component, computed, Inject, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PersonGroupsModel } from '@models/person-groups.model';
import { UiButtonComponent, UiModels, UiNoDataComponent, UiPersonTileComponent, UiSearchComponent } from '@ui';
import { DatabaseTabs, DatabaseTabsComponent } from '../../../../components/database-tabs/database-tabs.component';
import { Store } from '@ngrx/store';
import { personFeature } from '@states/person/person.feature';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { PersonService } from '../../../../../../services/person.service';
import { FormsModule } from '@angular/forms';
import { UiPreloaderComponent } from '../../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { SharedModels } from '@share-models';
import { features } from '@consts/text.const';
import { PersonModel } from '@models/person.model';
import { Actions, ofType } from '@ngrx/effects';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-add-person-to-group-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    UiButtonComponent,
    DatabaseTabsComponent,
    UiSearchComponent,
    UiPreloaderComponent,
    UiNoDataComponent,
    UiPersonTileComponent,
    FormsModule,
  ],
  templateUrl: './add-person-to-group-dialog.component.html',
  styleUrl: './add-person-to-group-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPersonToGroupDialogComponent {
  private readonly store$ = inject(Store);
  private readonly personService = inject(PersonService);
  private readonly translateService = inject(TranslateService);
  private readonly actions$ = inject(Actions);

  protected readonly tabs = PersonGroupsModel.AddPersonToGroupDialogTab;
  protected readonly personTrainingStatus = PersonModel.TrainingStatus;
  protected readonly currentTab = signal<PersonGroupsModel.AddPersonToGroupDialogTab>(
    PersonGroupsModel.AddPersonToGroupDialogTab.SavedPersons,
  );
  protected readonly searchQuery = signal<string>('');
  protected readonly viewState = SharedModels.ViewState;
  protected readonly preloaderColor = UiModels.PreloaderColor;
  protected readonly personNoData = features.person;

  private readonly savedPersons = this.store$.selectSignal(personFeature.selectAllSavedPersons);
  private readonly unsavedPersons = this.store$.selectSignal(personFeature.selectAllUnsavedPersons);

  private readonly savedPersonsTotal = this.store$.selectSignal(personFeature.selectSavedPersonsTotal);
  private readonly unsavedPersonsTotal = this.store$.selectSignal(personFeature.selectUnsavedPersonsTotal);

  protected readonly isSavedPersonsLoading = this.store$.selectSignal(personFeature.selectIsLoadingSavedPersons);
  protected readonly isUnsavedPersonsLoading = this.store$.selectSignal(personFeature.selectIsLoadingUnsavedPersons);
  protected readonly isAddingPersonsToGroups = this.store$.selectSignal(personGroupsFeature.selectIsAddingPersonsToGroups);

  protected readonly dialogTabs: DatabaseTabs<PersonGroupsModel.AddPersonToGroupDialogTab>[] = [
    {
      value: PersonGroupsModel.AddPersonToGroupDialogTab.SavedPersons,
      labelKey: 'PERSONS.SAVED_PERSONS',
      iconName: 'ui-icon-users',
      count: this.savedPersonsTotal,
    },
    {
      value: PersonGroupsModel.AddPersonToGroupDialogTab.UnsavedPersons,
      labelKey: 'PERSONS.UNSAVED_PERSONS',
      iconName: 'ui-icon-add-person',
      count: this.unsavedPersonsTotal,
    },
  ];

  protected readonly searchPlaceholderKey = 'PERSON_GROUPS.ADD_PERSONS_DIALOG.SEARCH_PEOPLE';

  protected readonly filteredSavedPersons = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const memberIds = this.personGroupMemberIdsSet();
    const filtered = this.savedPersons().filter((person) => {
      if (!search) return true;
      return (person.name ?? '').toLowerCase().includes(search);
    });

    return [...filtered].sort((a, b) => {
      const aDisabled = memberIds.has(a.personId);
      const bDisabled = memberIds.has(b.personId);
      return Number(aDisabled) - Number(bDisabled);
    });
  });

  protected readonly filteredUnsavedPersons = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const persons = this.unsavedPersons();

    if (!search) {
      return persons;
    }

    return persons.filter((person) => this.getUnsavedPersonLabel(person.appearances).toLowerCase().includes(search));
  });

  protected readonly savedPersonsViewState = computed<SharedModels.ViewState>(() => {
    const isLoading = this.isSavedPersonsLoading();
    const hasSearch = this.searchQuery().trim().length > 0;
    const filteredPersons = this.filteredSavedPersons();

    if (isLoading) return SharedModels.ViewState.INITIAL_EMPTY;
    if (filteredPersons.length > 0) return SharedModels.ViewState.HAS_CONTENT;
    if (hasSearch) return SharedModels.ViewState.NO_RESULTS;
    return SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected readonly unsavedPersonsViewState = computed<SharedModels.ViewState>(() => {
    const isLoading = this.isUnsavedPersonsLoading();
    const hasSearch = this.searchQuery().trim().length > 0;
    const filteredPersons = this.filteredUnsavedPersons();

    if (isLoading) return SharedModels.ViewState.INITIAL_EMPTY;
    if (filteredPersons.length > 0) return SharedModels.ViewState.HAS_CONTENT;
    if (hasSearch) return SharedModels.ViewState.NO_RESULTS;
    return SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected readonly personGroupMemberIdsSet = computed(
    () => new Set(this.data.personGroupMemberIds),
  );
  protected readonly selectedSavedPersonIds = signal<Set<number>>(new Set<number>());
  protected readonly selectedUnsavedPersonIds = signal<Set<number>>(new Set<number>());

  protected readonly selectedItemsCount = computed(
    () => this.selectedSavedPersonIds().size + this.selectedUnsavedPersonIds().size,
  );

  protected readonly addSelectedDisabled = computed(() => this.selectedItemsCount() === 0 || this.isAddingPersonsToGroups());

  public constructor(
    private readonly dialogRef: MatDialogRef<AddPersonToGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: PersonGroupsModel.AddPersonToGroupDialogData,
  ) {}

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected changeActiveTab(tab: PersonGroupsModel.AddPersonToGroupDialogTab): void {
    this.currentTab.set(tab);
    this.searchQuery.set('');
  }

  protected getImagePath(
    person: PersonModel.SavedPerson | PersonModel.UnsavedPerson,
    gcp: boolean = true,
  ): string {
    const isSaved = 'personId' in person;
    return this.personService.getPersonImagePath(person.bestImage, isSaved, gcp);
  }

  protected getUnsavedPersonLabel(appearances: number): string {
    return `${appearances} ${this.translateService.instant('PEOPLE_UNSAVED_LIST.RECOGNITIONS')}`;
  }

  protected isSavedPersonSelected(personId: number): boolean {
    return this.selectedSavedPersonIds().has(personId);
  }

  protected isUnsavedPersonSelected(bucketId: number): boolean {
    return this.selectedUnsavedPersonIds().has(bucketId);
  }

  protected isPersonExistedInGroup(personId: number): boolean {
    return this.personGroupMemberIdsSet().has(personId);
  }

  protected updateSavedPersonSelection(personId: number, selected: boolean): void {
    this.selectedSavedPersonIds.update((selectedPersonIds) => {
      const nextSelectedPersonIds = new Set(selectedPersonIds);
      if (selected) {
        nextSelectedPersonIds.add(personId);
      } else {
        nextSelectedPersonIds.delete(personId);
      }
      return nextSelectedPersonIds;
    });
  }

  protected updateUnsavedPersonSelection(bucketId: number, selected: boolean): void {
    this.selectedUnsavedPersonIds.update((selectedPersonIds) => {
      const nextSelectedPersonIds = new Set(selectedPersonIds);
      if (selected) {
        nextSelectedPersonIds.add(bucketId);
      } else {
        nextSelectedPersonIds.delete(bucketId);
      }
      return nextSelectedPersonIds;
    });
  }

  protected onAddSelected(): void {
    if (this.addSelectedDisabled()) {
      return;
    }
    this.actions$
      .pipe(
        ofType(PersonGroupsActions.addPersonsToGroupsSuccess),
        filter(({ groupIds }) => groupIds.includes(this.data.groupId)),
        take(1),
      )
      .subscribe(() => {
        this.dialogRef.close();
      });

    const unsavedPersonDtos = Array.from(this.selectedUnsavedPersonIds()).map((bucketId, index) => ({
      bucketId,
      name: PersonModel.getDefaultPersonName(index + 1),
    }));

    this.store$.dispatch(
      PersonGroupsActions.startAddingPersonsToGroups({
        groupIds: [this.data.groupId],
        savedPersonIds: Array.from(this.selectedSavedPersonIds()),
        unsavedPersonDtos,
      }),
    );
  }
}
